// Phone-number helpers for the web onboarding contact step, mirroring the mobile
// app: default the country from the browser locale, format the national number as
// it is typed (with the North American grouping), and convert to strict E.164.
import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  isSupportedCountry,
  parsePhoneNumberFromString,
} from "libphonenumber-js/min";

const DEFAULT_COUNTRY = "US";
const PINNED_COUNTRIES = ["US", "CA", "AU", "NZ", "GB"];
const CROWN_DEPENDENCIES = { GG: "GB", IM: "GB", JE: "GB" };
const MAX_DIGITS = 15;

export function defaultCountry(region) {
  const code = (region || "").toUpperCase();
  if (!code) return DEFAULT_COUNTRY;
  if (CROWN_DEPENDENCIES[code]) return CROWN_DEPENDENCIES[code];
  return isSupportedCountry(code) ? code : DEFAULT_COUNTRY;
}

export function flagEmoji(country) {
  return country.toUpperCase().replace(/./g, (character) => String.fromCodePoint(127397 + character.charCodeAt(0)));
}

export function countryName(country, locale) {
  try {
    return new Intl.DisplayNames([locale || "en"], { type: "region" }).of(country) || country;
  } catch {
    return country;
  }
}

// The full country list with the primary English-speaking markets pinned first,
// then everyone else alphabetically by localized name.
export function countryOptions(locale) {
  const all = getCountries();
  const pinned = PINNED_COUNTRIES.filter((code) => all.includes(code));
  const rest = all
    .filter((code) => !pinned.includes(code))
    .map((code) => ({ code, name: countryName(code, locale) }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return [...pinned.map((code) => ({ code, name: countryName(code, locale) })), ...rest].map(({ code, name }) => ({
    code,
    name,
    callingCode: getCountryCallingCode(code),
    flag: flagEmoji(code),
  }));
}

export function digitsOf(text) {
  return (text || "").replace(/\D/g, "").slice(0, MAX_DIGITS);
}

// The national digits for a country, capped so a number can never grow past a
// valid length. North America is a fixed ten digits, with a leading trunk "1"
// dropped; every other country is bounded by the E.164 ceiling once its calling
// code is counted.
function nationalDigits(input, country) {
  const callingCode = getCountryCallingCode(country);
  const digits = digitsOf(input);
  if (callingCode === "1") {
    const national = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
    return national.slice(0, 10);
  }
  return digits.slice(0, Math.max(0, MAX_DIGITS - callingCode.length));
}

export function format(input, country) {
  const digits = nationalDigits(input, country);
  if (!digits) return "";
  if (getCountryCallingCode(country) === "1") return formatNanp(digits);
  return new AsYouType(country).input(digits);
}

// Convert a national number plus its country to strict E.164, or null when the
// number is not valid for that country.
export function toE164(input, country) {
  const parsed = parsePhoneNumberFromString(nationalDigits(input, country), country);
  return parsed && parsed.isValid() ? parsed.number : null;
}

// Split a stored E.164 number back into its country and national digits, so an
// existing number can seed the country picker and the as-you-type input. Returns
// null when the string is absent or is not a parseable E.164 number.
export function fromE164(e164) {
  const parsed = parsePhoneNumberFromString((e164 || "").trim());
  if (!parsed || !parsed.country) return null;
  return { country: parsed.country, national: parsed.nationalNumber };
}

function formatNanp(digits) {
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}
